import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Clock,
  Envelope,
  Phone,
  MapPin,
  Car,
  Package,
  Warning,
  ArrowLeft,
} from '@phosphor-icons/react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'nouveau', label: 'Demande reçue', description: 'Votre demande a bien été enregistrée.' },
  { key: 'en_cours', label: 'En cours de traitement', description: 'Notre équipe recherche vos pièces.' },
  { key: 'valide', label: 'Pièces validées', description: 'Les pièces sont confirmées disponibles.' },
  { key: 'devis_envoye', label: 'Devis envoyé', description: 'Le devis a été envoyé par email.' },
  { key: 'cloture', label: 'Clôturé', description: 'Votre demande est finalisée.' },
];

const formatDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const TrackQuotePage = () => {
  const { quoteId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/quotes/track/${quoteId}`);
        if (mounted) { setData(resp.data); setLoading(false); }
      } catch (e) {
        if (mounted) { setError(e.response?.status === 404 ? 'Demande introuvable. Vérifiez le lien dans votre email.' : 'Erreur lors du chargement.'); setLoading(false); }
      }
    };
    load();
    return () => { mounted = false; };
  }, [quoteId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <Header />
        <div className="max-w-2xl mx-auto pt-32 pb-24 px-6 text-center">
          <div className="bg-white border border-gray-200 p-10 shadow-sm">
            <Warning size={48} className="text-amber-500 mx-auto mb-4" />
            <h1 className="font-['Outfit'] text-2xl font-bold text-gray-900 mb-3">Suivi indisponible</h1>
            <p className="text-gray-500 mb-6">{error || 'Demande introuvable.'}</p>
            <Link to="/" className="inline-block px-6 py-3 bg-[#E63946] text-white text-sm font-bold uppercase tracking-wide hover:opacity-90" data-testid="back-home">
              Retour à l'accueil
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { quote, analysis } = data;
  const currentStepIdx = Math.max(0, STEPS.findIndex(s => s.key === quote.status));
  const detectedParts = analysis?.detected_parts || [];
  const selectedDetails = quote.selected_parts.map(name => detectedParts.find(p => p.part_name === name)).filter(Boolean);
  const totalPrice = selectedDetails.reduce((sum, p) => sum + (p.stock_price || p.estimated_market_price || 0), 0);

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-900 text-sm mb-6" data-testid="back-link">
            <ArrowLeft size={16} />Accueil
          </Link>

          {/* Header card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-200 p-6 md:p-8 shadow-sm mb-6" data-testid="quote-track-header">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <span className="text-[#E63946] text-xs uppercase tracking-[0.3em] font-bold">Suivi de devis</span>
                <h1 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-gray-900 mt-2">
                  Bonjour {quote.first_name} !
                </h1>
                <p className="text-gray-500 mt-1">
                  Référence : <span className="font-mono text-gray-900">#{quote.id.slice(0, 8).toUpperCase()}</span>
                </p>
              </div>
              <div className="text-left md:text-right">
                <p className="text-gray-400 text-xs uppercase tracking-wider">Demande créée le</p>
                <p className="text-gray-900 font-semibold">{formatDate(quote.created_at)}</p>
              </div>
            </div>
          </motion.div>

          {/* Status timeline */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white border border-gray-200 p-6 md:p-8 shadow-sm mb-6" data-testid="quote-timeline">
            <h2 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-6">État d'avancement</h2>
            <div className="space-y-1">
              {STEPS.map((step, i) => {
                const isDone = i < currentStepIdx;
                const isCurrent = i === currentStepIdx;
                const isPending = i > currentStepIdx;
                return (
                  <div key={step.key} className="flex gap-4" data-testid={`step-${step.key}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-full border-2 transition-colors ${
                        isDone ? 'bg-[#2A9D8F] border-[#2A9D8F] text-white' :
                        isCurrent ? 'bg-[#E63946] border-[#E63946] text-white animate-pulse' :
                        'bg-white border-gray-300 text-gray-300'
                      }`}>
                        {isDone ? <CheckCircle size={18} weight="fill" /> : <span className="text-sm font-bold">{i + 1}</span>}
                      </div>
                      {i < STEPS.length - 1 && (
                        <div className={`w-0.5 h-12 ${isDone ? 'bg-[#2A9D8F]' : 'bg-gray-200'}`} />
                      )}
                    </div>
                    <div className={`flex-1 pb-8 ${isPending ? 'opacity-40' : ''}`}>
                      <p className={`font-semibold text-sm ${isCurrent ? 'text-[#E63946]' : isDone ? 'text-[#2A9D8F]' : 'text-gray-900'}`}>
                        {step.label}
                        {isCurrent && <span className="ml-2 text-xs font-normal bg-[#E63946]/10 px-2 py-0.5">En cours</span>}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {quote.updated_at && quote.updated_at !== quote.created_at && (
              <p className="text-gray-400 text-xs mt-4 flex items-center gap-2 pt-4 border-t border-gray-100">
                <Clock size={12} />Dernière mise à jour : {formatDate(quote.updated_at)}
              </p>
            )}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Vehicle & contact info */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-2 space-y-6">
              {/* Selected parts */}
              <div className="bg-white border border-gray-200 p-6 shadow-sm" data-testid="selected-parts-section">
                <h3 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Package size={20} className="text-[#E63946]" />
                  Pièces demandées ({quote.selected_parts.length})
                </h3>
                <div className="space-y-2">
                  {selectedDetails.length > 0 ? selectedDetails.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100">
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium text-sm">{p.part_name}</p>
                        <p className="text-gray-400 text-xs">{p.category}{p.side && ` • ${p.side}`}</p>
                        {p.stock_ecocasse?.length > 0 && (
                          <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 bg-[#E63946] text-white font-bold">EN STOCK ECOCASSE</span>
                        )}
                      </div>
                      <span className="font-bold text-[#E63946] text-sm whitespace-nowrap">
                        ~ {(p.stock_price || p.estimated_market_price || 0).toFixed(2)} €
                      </span>
                    </div>
                  )) : quote.selected_parts.map((name, i) => (
                    <div key={i} className="p-3 bg-gray-50 border border-gray-100 text-sm text-gray-700">{name}</div>
                  ))}
                </div>
                {totalPrice > 0 && (
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                    <span className="text-gray-500 text-sm">Estimation indicative</span>
                    <span className="text-[#E63946] font-bold text-xl">{totalPrice.toFixed(2)} €</span>
                  </div>
                )}
                <p className="text-gray-400 text-xs mt-2 italic">Devis définitif après vérification par nos équipes.</p>
              </div>

              {/* Vehicle */}
              {analysis && (analysis.brand || analysis.vehicle_identified?.brand) && (
                <div className="bg-white border border-gray-200 p-6 shadow-sm" data-testid="vehicle-section">
                  <h3 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Car size={20} className="text-[#E63946]" />Véhicule concerné
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Marque / Modèle</p>
                      <p className="text-gray-900 font-medium">
                        {analysis.brand || analysis.vehicle_identified?.brand || '-'} {analysis.model || analysis.vehicle_identified?.model || ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Année</p>
                      <p className="text-gray-900 font-medium">{analysis.year || analysis.vehicle_identified?.year || '-'}</p>
                    </div>
                    {analysis.registration && (
                      <div>
                        <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Immatriculation</p>
                        <p className="text-gray-900 font-mono uppercase">{analysis.registration}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Contact info */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="bg-white border border-gray-200 p-6 shadow-sm h-fit" data-testid="contact-section">
              <h3 className="font-['Outfit'] text-base font-bold text-gray-900 mb-4">Vos coordonnées</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3"><Envelope size={16} className="text-gray-400 mt-0.5" /><span className="text-gray-700 break-all">{quote.email}</span></div>
                {quote.phone && <div className="flex items-center gap-3"><Phone size={16} className="text-gray-400" /><span className="text-gray-700">{quote.phone}</span></div>}
                {quote.city && <div className="flex items-center gap-3"><MapPin size={16} className="text-gray-400" /><span className="text-gray-700">{quote.city}</span></div>}
              </div>
              <p className="text-gray-400 text-xs mt-6 leading-relaxed">
                Une question sur votre demande ? Répondez simplement à l'email de confirmation.
              </p>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TrackQuotePage;
